/**
 * WalletLanding - Entry page for the wallet mini app
 *
 * Design: ALOV Champion Club branding
 * - Deep purple gradient background
 * - Logo with subtle 3D emerge animation
 * - Two CTAs: Deposit (primary) and Withdrawal (secondary)
 *
 * Transition: When button pressed, animates to "in-between" state
 * - Logo moves up
 * - Sparkles rotate clockwise
 * - Buttons slide down (primary grows first)
 */

import { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';

// ============================================
// TYPES
// ============================================

export type TransitionTarget = 'deposit' | 'withdraw' | null;

export interface WalletLandingProps {
  /** Handler for deposit button click (called after transition) */
  onDeposit?: () => void;
  /** Handler for withdrawal button click (called after transition) */
  onWithdraw?: () => void;
  /** Start in transitioned state (for deposit/withdraw pages) */
  transitioned?: boolean;
}

// ============================================
// COMPONENT
// ============================================

export function WalletLanding({
  onDeposit,
  onWithdraw,
  transitioned = false,
}: WalletLandingProps) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<TransitionTarget>(null);

  const handleDeposit = useCallback(() => {
    setTransitionTarget('deposit');
    setIsTransitioning(true);
    // Call handler after animation completes
    setTimeout(() => {
      onDeposit?.();
    }, 600);
  }, [onDeposit]);

  const handleWithdraw = useCallback(() => {
    setTransitionTarget('withdraw');
    setIsTransitioning(true);
    // Call handler after animation completes
    setTimeout(() => {
      onWithdraw?.();
    }, 600);
  }, [onWithdraw]);

  const showTransitioned = transitioned || isTransitioning;

  return (
    <div className={cn(
      'wallet-landing',
      showTransitioned && 'wallet-landing--transitioned'
    )}>
      {/* Background */}
      <div className="wallet-landing__bg" />

      {/* Animated sparkles */}
      <div className={cn(
        'wallet-sparkles',
        showTransitioned && 'wallet-sparkles--rotated'
      )}>
        <span className="sparkle sparkle--1" />
        <span className="sparkle sparkle--2" />
        <span className="sparkle sparkle--3" />
        <span className="sparkle sparkle--4" />
        <span className="sparkle sparkle--5" />
        <span className="sparkle sparkle--6" />
        <span className="sparkle sparkle--7" />
        <span className="sparkle sparkle--8" />
        <span className="sparkle sparkle--9" />
        <span className="sparkle sparkle--10" />
        <span className="sparkle sparkle--11" />
      </div>

      {/* Content */}
      <div className="wallet-landing__content">
        {/* Logo Section */}
        <div className={cn(
          'wallet-landing__logo-section',
          showTransitioned && 'wallet-landing__logo-section--up'
        )}>
          <img
            src="/images/alov-logo.png"
            alt="ALOV Champion Club"
            className="wallet-landing__logo"
          />
        </div>

        {/* CTA Section */}
        <div className={cn(
          'wallet-landing__cta-section',
          showTransitioned && 'wallet-landing__cta-section--down'
        )}>
          {/* Primary CTA - Deposit */}
          <button
            type="button"
            onClick={handleDeposit}
            className={cn(
              'wallet-landing__btn-primary',
              isTransitioning && transitionTarget === 'deposit' && 'wallet-landing__btn-primary--grow'
            )}
          >
            QAZAN!
          </button>

          {/* Secondary CTA - Withdrawal */}
          <button
            type="button"
            onClick={handleWithdraw}
            className="wallet-landing__btn-secondary"
          >
            UDUŞUNU ÇIXAR
          </button>
        </div>
      </div>
    </div>
  );
}
